import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, Mail, RefreshCw } from 'lucide-react';
import Header from '@/components/layout/Header';
import FooterNew from '@/components/layout/FooterNew';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { API, toast } from '@/App';
import { getAuthToken } from '@/utils/authSession';

const RESEND_SUCCESS_MESSAGE = 'Un nouvel email de vérification vous a été envoyé.';
const RESEND_ERROR_MESSAGE = "Impossible d’envoyer l’email pour le moment, réessayez plus tard.";

const normalizeUserRole = (role) => {
  const normalizedRole = String(role || '').trim().toLowerCase();
  if (normalizedRole === 'courier') return 'livreur';
  if (normalizedRole === 'vendor') return 'vendeur';
  if (normalizedRole === 'agent-pr' || normalizedRole === 'agentpr' || normalizedRole === 'relay_agent') return 'agent_pr';
  if (normalizedRole === 'affiliate') return 'ambassador';
  return normalizedRole;
};

const resolveDashboardPath = (role) => {
  switch (normalizeUserRole(role)) {
    case 'admin':
      return '/admin';
    case 'vendeur':
      return '/vendeur';
    case 'livreur':
      return '/livreur';
    case 'agent_pr':
      return '/agent-pr';
    case 'ambassador':
    case 'super_ambassador':
      return '/affiliate/dashboard';
    default:
      return '/';
  }
};

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);
  const [status, setStatus] = useState('pending');
  const [message, setMessage] = useState("Un lien de verification vous a ete envoye.");
  const [currentUser, setCurrentUser] = useState(null);

  const token = useMemo(() => {
    const params = new URLSearchParams(location.search || window.location.search || '');
    return String(params.get('token') || '').trim();
  }, [location.search]);

  const storedToken = getAuthToken();
  const emailHint = currentUser?.email || location.state?.email || '';
  const roleHint = currentUser?.role || location.state?.role || '';

  useEffect(() => {
    let active = true;

    const loadCurrentUser = async () => {
      if (!getAuthToken()) {
        if (active) {
          setCurrentUser(null);
        }
        return null;
      }

      try {
        const response = await axios.get(`${API}/auth/me`);
        if (active) {
          setCurrentUser(response.data);
        }
        return response.data;
      } catch (_error) {
        if (active) {
          setCurrentUser(null);
        }
        return null;
      }
    };

    const bootstrap = async () => {
      setLoading(true);
      const me = await loadCurrentUser();

      if (!active) {
        return;
      }

      if (!token) {
        if (me?.email_verified === true) {
          setStatus('already_verified');
          setMessage('Votre email est deja verifie.');
        } else {
          setStatus('pending');
          setMessage("Consultez votre boite mail puis cliquez sur le lien de verification securise.");
        }
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/verify-email`, {
          params: { token },
        });
        const payload = response.data?.data || {};
        if (!active) {
          return;
        }
        setStatus(payload.already_verified ? 'already_verified' : 'success');
        setMessage(response.data?.message || 'Email verifie avec succes.');
        if (getAuthToken()) {
          await loadCurrentUser();
        }
        window.dispatchEvent(new Event('cnk-auth-refresh'));
      } catch (error) {
        if (!active) {
          return;
        }
        const detail = error.response?.data?.detail || "Impossible de verifier ce lien.";
        if (error.response?.status === 410) {
          setStatus('expired');
        } else {
          setStatus('invalid');
        }
        setMessage(detail);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    bootstrap();

    return () => {
      active = false;
    };
  }, [token]);

  const handleResend = async () => {
    if (!getAuthToken()) {
      toast.error('Reconnectez-vous pour renvoyer un email de verification.');
      navigate('/');
      return;
    }

    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`);
      setStatus('pending');
      setMessage(RESEND_SUCCESS_MESSAGE);
      toast.success(RESEND_SUCCESS_MESSAGE);
    } catch (_error) {
      setMessage(RESEND_ERROR_MESSAGE);
      toast.error(RESEND_ERROR_MESSAGE);
    } finally {
      setResending(false);
    }
  };

  const renderState = () => {
    if (loading) {
      return (
        <div className="py-10 text-center" data-testid="verify-email-loading">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-red-100 border-t-red-500" />
          <p className="mt-4 text-sm text-slate-600">Verification en cours...</p>
        </div>
      );
    }

    const successState = status === 'success' || status === 'already_verified';
    const errorState = status === 'expired' || status === 'invalid';

    return (
      <div className="space-y-6" data-testid={`verify-email-state-${status}`}>
        <div
          className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${
            successState ? 'bg-emerald-100 text-emerald-700' : errorState ? 'bg-rose-100 text-rose-700' : 'bg-cyan-100 text-cyan-700'
          }`}
        >
          {successState ? (
            <CheckCircle2 className="h-8 w-8" />
          ) : errorState ? (
            <AlertTriangle className="h-8 w-8" />
          ) : (
            <Mail className="h-8 w-8" />
          )}
        </div>

        <div className="space-y-2 text-center">
          <CardTitle className="text-2xl">
            {status === 'success' && 'Email verifie'}
            {status === 'already_verified' && 'Email deja verifie'}
            {status === 'expired' && 'Lien expire'}
            {status === 'invalid' && 'Lien invalide'}
            {status === 'pending' && 'Verifiez votre email'}
          </CardTitle>
          <CardDescription className="text-base text-slate-600">
            {message}
          </CardDescription>
          {emailHint ? (
            <p className="text-sm text-slate-500" data-testid="verify-email-address">
              Adresse concernee : <span className="font-medium text-slate-700">{emailHint}</span>
            </p>
          ) : null}
        </div>

        <div className="space-y-3">
          {(status === 'pending' || status === 'expired' || status === 'invalid') && storedToken ? (
            <Button
              type="button"
              className="w-full bg-red-500 hover:bg-red-600"
              onClick={handleResend}
              disabled={resending}
              data-testid="verify-email-resend"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${resending ? 'animate-spin' : ''}`} />
              {resending ? 'Envoi...' : 'Renvoyer email'}
            </Button>
          ) : null}

          {successState ? (
            <Button
              type="button"
              className="w-full bg-cyan-600 hover:bg-cyan-700"
              onClick={() => navigate(resolveDashboardPath(roleHint))}
              data-testid="verify-email-continue"
            >
              <ArrowRight className="mr-2 h-4 w-4" />
              {storedToken ? 'Acceder a mon espace' : 'Aller a la connexion'}
            </Button>
          ) : null}

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => navigate(storedToken ? resolveDashboardPath(roleHint) : '/')}
            data-testid="verify-email-back"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {storedToken ? 'Retour a mon espace' : 'Retour a la connexion'}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-cyan-50">
      <Header />

      <main className="mx-auto flex min-h-[calc(100vh-180px)] max-w-6xl items-center px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid w-full gap-8 lg:grid-cols-[1.2fr_0.8fr]">
          <section className="space-y-6">
            <div className="inline-flex items-center rounded-full border border-red-100 bg-white/80 px-4 py-2 text-sm font-medium text-red-700 shadow-sm">
              Verification email Coli Noki
            </div>
            <div className="space-y-4">
              <h1 className="max-w-2xl text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl">
                Une verification simple, securisee et indispensable.
              </h1>
              <p className="max-w-2xl text-lg text-slate-600">
                Votre email verifie devient la preuve de contact de reference pour les actions sensibles, le wallet,
                les paiements et les futures briques de securite comme la 2FA.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-white/80 bg-white/90 p-4 shadow-sm">
                <p className="text-sm font-semibold text-slate-900">Lien securise</p>
                <p className="mt-2 text-sm text-slate-600">Token unique, usage unique et expiration automatique.</p>
              </div>
              <div className="rounded-2xl border border-white/80 bg-white/90 p-4 shadow-sm">
                <p className="text-sm font-semibold text-slate-900">Système central</p>
                <p className="mt-2 text-sm text-slate-600">Le statut verifié provient toujours du système central Coli Noki.</p>
              </div>
              <div className="rounded-2xl border border-white/80 bg-white/90 p-4 shadow-sm">
                <p className="text-sm font-semibold text-slate-900">Pret pour la suite</p>
                <p className="mt-2 text-sm text-slate-600">La base est prete pour 2FA et des controles renforces.</p>
              </div>
            </div>
          </section>

          <section>
            <Card className="border-0 bg-white/95 shadow-2xl shadow-red-100/50">
              <CardHeader className="pb-2">
                <CardTitle>Confirmation de votre email</CardTitle>
                <CardDescription>
                  Utilisez le lien envoye par Coli Noki, ou demandez un nouveau message si besoin.
                </CardDescription>
              </CardHeader>
              <CardContent>{renderState()}</CardContent>
            </Card>
          </section>
        </div>
      </main>

      <FooterNew />
    </div>
  );
};

export default VerifyEmailPage;
